import React, { useState, useEffect } from 'react';
import { fetchStaffProfiles, updateStaffProfile } from '../api';

const StaffConfigurationModal = ({ authAxios, addToast, onClose }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const data = await fetchStaffProfiles(authAxios);
      setProfiles(data.results || data);
    } catch {
      addToast('Failed to load profiles', 'error');
    }
    setLoading(false);
  };

  const handleEdit = (p) => {
    setEditId(p.id);
    setFormData({
      basic_salary: p.basic_salary || '',
      housing_allowance: p.housing_allowance || '',
      transport_allowance: p.transport_allowance || '',
      medical_aid_contribution: p.medical_aid_contribution || '',
      pension_percentage: p.pension_percentage || '',
      id_number: p.id_number || '',
      pension_membership_number: p.pension_membership_number || '',
    });
  };

  const handleSave = async (id) => {
    try {
      await updateStaffProfile(authAxios, id, formData);
      addToast('Updated profile successfully', 'success');
      setEditId(null);
      loadProfiles();
    } catch {
      addToast('Failed to update profile', 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-auto shadow-2xl">
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h3 className="text-2xl font-bold text-gray-800">Staff Payroll Configuration</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl font-bold">&times;</button>
        </div>
        {loading ? <div className="text-center p-4">Loading profiles...</div> : (
          <table className="w-full text-left border-collapse text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border-b p-3 font-semibold text-gray-700">Staff Member</th>
                <th className="border-b p-3 font-semibold text-gray-700">Basic Salary (BWP)</th>
                <th className="border-b p-3 font-semibold text-gray-700">Housing Allowance</th>
                <th className="border-b p-3 font-semibold text-gray-700">Transport</th>
                <th className="border-b p-3 font-semibold text-gray-700">Medical Aid</th>
                <th className="border-b p-3 font-semibold text-gray-700">Pension %</th>
                <th className="border-b p-3 font-semibold text-gray-700">Omang / ID</th>
                <th className="border-b p-3 font-semibold text-gray-700">Pension Membership #</th>
                <th className="border-b p-3 font-semibold text-gray-700 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(p => (
                <tr key={p.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-800">
                    {p.user?.first_name} {p.user?.last_name} 
                    <span className="text-xs text-gray-500 block">{p.user?.email || p.user?.username}</span>
                  </td>
                  {editId === p.id ? (
                    <>
                      <td className="p-2"><input type="number" className="w-[85px] border rounded px-2 py-1" value={formData.basic_salary} onChange={e => setFormData({...formData, basic_salary: e.target.value})} /></td>
                      <td className="p-2"><input type="number" className="w-[85px] border rounded px-2 py-1" value={formData.housing_allowance} onChange={e => setFormData({...formData, housing_allowance: e.target.value})} /></td>
                      <td className="p-2"><input type="number" className="w-[85px] border rounded px-2 py-1" value={formData.transport_allowance} onChange={e => setFormData({...formData, transport_allowance: e.target.value})} /></td>
                      <td className="p-2"><input type="number" className="w-[85px] border rounded px-2 py-1" value={formData.medical_aid_contribution} onChange={e => setFormData({...formData, medical_aid_contribution: e.target.value})} /></td>
                      <td className="p-2"><input type="number" step="0.1" className="w-[70px] border rounded px-2 py-1" value={formData.pension_percentage} onChange={e => setFormData({...formData, pension_percentage: e.target.value})} /></td>
                      <td className="p-2"><input type="text" className="w-[100px] border rounded px-2 py-1" value={formData.id_number} onChange={e => setFormData({...formData, id_number: e.target.value})} placeholder="Omang / Passport" /></td>
                      <td className="p-2"><input type="text" className="w-[100px] border rounded px-2 py-1" value={formData.pension_membership_number} onChange={e => setFormData({...formData, pension_membership_number: e.target.value})} placeholder="BPOPF #" /></td>
                      <td className="p-2 text-center">
                        <button onClick={() => handleSave(p.id)} className="bg-blue-600 text-white px-3 py-1 rounded shadow hover:bg-blue-700 mr-2 text-xs transition">Save</button>
                        <button onClick={() => setEditId(null)} className="bg-gray-400 text-white px-3 py-1 rounded shadow hover:bg-gray-500 text-xs transition">Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3">BWP {parseFloat(p.basic_salary || 0).toFixed(2)}</td>
                      <td className="p-3">BWP {parseFloat(p.housing_allowance || 0).toFixed(2)}</td>
                      <td className="p-3">BWP {parseFloat(p.transport_allowance || 0).toFixed(2)}</td>
                      <td className="p-3">BWP {parseFloat(p.medical_aid_contribution || 0).toFixed(2)}</td>
                      <td className="p-3">{parseFloat(p.pension_percentage || 0).toFixed(2)}%</td>
                      <td className="p-3">{p.id_number || <span className="text-gray-400 italic">—</span>}</td>
                      <td className="p-3">{p.pension_membership_number || <span className="text-gray-400 italic">—</span>}</td>
                      <td className="p-3 text-center">
                        <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-800 font-medium text-sm hover:underline">Edit</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {profiles.length === 0 && (
                <tr>
                  <td colSpan="9" className="p-4 text-center text-gray-500">No staff profiles found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StaffConfigurationModal;